// constants
import { ENTITIES } from 'constants/entities'
import { FROM_PARENT_FIELD } from 'constants/systemProcesses'
import { FORM_SELECT_SECURITY_OPTIONS } from 'constants/security'
import { DATE_OPTIONS, DATE_SPECIAL_TYPES } from 'components/FormElements/DatePicker/components/CustomToolbar'
// lodash
import { flatten, fromPairs, isEmpty, isNil, isUndefined, omit } from 'lodash'
import { filter, find, get, getOr, isFunction, map as mapFp, pipe, uniq } from 'lodash/fp'
// types
import { IOption } from 'components/FormElements/Select'
import { ApolloClient } from '@apollo/client'
import { FieldSource, FieldType, IField, IFieldValueMapItem, IResponseTypesSet, IRiskTemplate } from 'types/graphql'
// utils
import { isUuid } from '../uuid'
import { ifElse } from '../fp'
import { prepareHtml } from 'utils/html'
import { handleError } from 'utils/errors/handlers'
import { getOptionByValue } from './select'
import { getOptionsBySource } from 'components/PageComponents/RiskTemplates/Edit/components/RiskTemplateFields/components/Lookup/utils'
import { getRiskFieldOptionLabel, getRiskFieldOptionTemplateId } from 'utils/form/risk'
import { getEntitySelectOptions, getEntitySelectQuery, getEntitySelectVariables } from '../fields'

export * from './select'
export * from './wysiwyg'
export * from './risk'

export const generateInitialOwnedByValue = (entity) => {
  if (entity.ownedByPosition) {
    return {
      label: entity.ownedByPosition.description,
      value: entity.ownedByPosition.id
    }
  }

  if (entity.ownedByUser) {
    return {
      label: `${entity.ownedByUser.firstName} ${entity.ownedByUser.lastName}`,
      value: entity.ownedByUser.id
    }
  }
}

export const generateFieldSource = (entity) => {
  const entityMap = {
    [ENTITIES.BUSINESS_ACTIVITY]: FieldSource.BusinessActivities,
    [ENTITIES.BUSINESS_PROCESS]: FieldSource.BusinessProcesses,
    [ENTITIES.LOCATION]: FieldSource.Locations,
    [ENTITIES.BUSINESS_UNIT]: FieldSource.BusinessUnits,
    [ENTITIES.PLAN]: FieldSource.Plans,
    [ENTITIES.PLAN_KPI]: FieldSource.Kpis,
    [ENTITIES.PLAN_ACTION]: FieldSource.Actions,
    [ENTITIES.PLAN_OBJECTIVE]: FieldSource.Objectives,
    [ENTITIES.PLAN_TYPE]: FieldSource.PlanTypes,
    [ENTITIES.POSITION]: FieldSource.Positions,
    [ENTITIES.USER]: FieldSource.Users,
    [ENTITIES.RISK]: FieldSource.Risks
  }

  return entityMap[entity]
}

export const getFieldValues = (fields: IField[], data: any, defaultObjectSecurities?: any) => {
  return fields
    .map((field) => {
      let fieldValue = data[field.name]

      if (isNil(fieldValue)) {
        if (field.fieldType === FieldType.Date || field.fieldType === FieldType.DocumentOrLink) {
          return { fieldId: field.id, value: null }
        }
        return
      }

      if (field.fieldType === FieldType.DocumentOrLink && (fieldValue as any)?._pending) {
        return
      }

      if (field.fieldType === FieldType.Tag) {
        fieldValue = data[field.name].map(prepareTagValue)
      }

      if (field.fieldType === FieldType.PickList) {
        if (Array.isArray(fieldValue)) {
          fieldValue = fieldValue.map((value) => {
            const option = field.options.find((option) => option.label === value.label)

            return option ? { value: option.id } : null
          })
        } else {
          const option = field.options.find((option) => option.label === fieldValue.label)
          fieldValue = option ? { value: option.id } : null
        }
      }

      if (fieldValue?.label) {
        fieldValue = omit(fieldValue, ['label', '__typename'])
      }

      if (Array.isArray(fieldValue) && !isEmpty(fieldValue)) {
        fieldValue = fieldValue.map((value) => ({ ...omit(value, ['label', '__typename']) }))
      }

      if (field.name === 'membersUserPosition') {
        if (fieldValue.some((value) => !value.objectSecurityId)) {
          fieldValue = fieldValue.map((value) => {
            if (!value.objectSecurityId) {
              return { ...value, objectSecurityId: defaultObjectSecurities.memberObjectSecurityId }
            }
            return value
          })
        }
      }

      if (field.wysiwyg) {
        fieldValue = prepareHtml(fieldValue)
      }

      return { fieldId: field.id, value: fieldValue }
    })
    .filter((field) => !isNil(field))
}

export const getFieldId = (fields: IField[], fieldName: string) => {
  return pipe(
    find((field: IField) => field.name === fieldName),
    get('id')
  )(fields)
}

export const getFieldsOptions = async ({
  client,
  fields,
  domain,
  isRisk,
  isPublic,
  fieldsValues
}: {
  client: ApolloClient
  fields: IField[]
  domain?: string
  isRisk?: boolean
  isPublic?: boolean
  fieldsValues: IFieldValueMapItem[]
}): Promise<IOption[]> => {
  const sources = pipe(
    filter(({ fieldType }) => fieldType === FieldType.Lookup),
    mapFp(({ name, source, searchTemplateIds }: IField) => {
      const fieldValue = fieldsValues.find(({ fieldName }) => fieldName === name)?.fieldValue
      const isPlanType = name === 'planType'

      if ((isEmpty(source) || searchTemplateIds) && fieldValue) {
        if (Array.isArray(fieldValue)) {
          return fieldValue.map((item) => generateFieldSource(item?.entity)).filter((entity) => entity)
        }
        return generateFieldSource(isPlanType ? ENTITIES.PLAN_TYPE : fieldValue.entity)
      }

      return source
    }),
    flatten,
    uniq
  )(fields)

  if (isEmpty(sources)) {
    return []
  }

  if (isRisk) {
    if (!sources.includes(FieldSource.Users)) {
      sources.push(FieldSource.Users)
    }

    if (!sources.includes(FieldSource.Positions)) {
      sources.push(FieldSource.Positions)
    }
  }

  const valuesIds = pipe(
    filter(({ fieldType, fieldValue }) => {
      if (Array.isArray(fieldValue)) {
        return fieldType === FieldType.Lookup
      } else {
        return fieldType === FieldType.Lookup && isUuid(fieldValue?.value)
      }
    }),
    mapFp(({ fieldValue }) => {
      if (Array.isArray(fieldValue)) {
        return fieldValue.filter((item) => item && isUuid(item.value)).map(({ value }) => value)
      }
      return fieldValue?.value
    }),
    flatten,
    (res) => {
      if (fields.some((field) => field.searchTemplateIds)) {
        return [...res, ...fields.filter((field) => field.searchTemplateIds).map((field) => field.id)]
      }
      return res
    }
  )(fieldsValues)

  const query = getEntitySelectQuery(sources, isPublic)
  const variables = getEntitySelectVariables({ sources, domain, ids: valuesIds, active: false })

  return (await client
    .query({
      query,
      variables,
      fetchPolicy: 'no-cache',
      errorPolicy: 'all'
    })
    .then((res) => flatten(getEntitySelectOptions()(res).map(({ options }) => [...options])))
    .catch((err) => handleError(err, 'getFieldsOptions'))) as IOption[]
}

export const getFieldsInitialValues = async ({
  client,
  fields,
  domain,
  isRisk = false,
  isPublic = false,
  fieldValues,
  responseTypeSets,
  parentRiskTemplate
}: {
  client: ApolloClient
  fields: IField[]
  domain?: string
  isRisk?: boolean
  isPublic?: boolean
  fieldValues: IFieldValueMapItem[]
  responseTypeSets?: IResponseTypesSet[]
  parentRiskTemplate?: IRiskTemplate
}) => {
  if (isEmpty(fieldValues)) {
    return {}
  }

  const fieldOptions =
    (await getFieldsOptions({ client, fields, domain, isRisk, isPublic, fieldsValues: fieldValues })) ?? []
  const userOptions =
    getOptionsBySource(fields, null, [FieldSource.Users, FieldSource.Positions, FieldSource.BusinessUnits])?.options ??
    []

  // a value inherited from a parent risk's field is stored as a literal placeholder rather than
  // resolved at creation time, so it needs a pseudo-option to carry its label
  const parentFieldOptions = (parentRiskTemplate?.fields ?? []).map((field) => ({
    value: FROM_PARENT_FIELD,
    sourceFieldId: field.id,
    label: `${parentRiskTemplate.name}: ${field.label}`
  }))

  // a special-relation value (e.g. "Link to reports to person") can also target a field that
  // belongs to the parent risk's template rather than the current one
  const parentSpecialRelationOptions = (
    getOptionsBySource(parentRiskTemplate?.fields ?? [], null, [
      FieldSource.Users,
      FieldSource.Positions,
      FieldSource.BusinessUnits
    ])?.options ?? []
  ).map((option) => ({
    ...option,
    label: `${parentRiskTemplate.name}: ${option.label}`
  }))

  const lookupOptions = [
    ...userOptions,
    ...fieldOptions,
    ...parentFieldOptions,
    ...parentSpecialRelationOptions,
    ...FORM_SELECT_SECURITY_OPTIONS
  ]

  const initialValues = fieldValues.map((fieldValueItem) => {
    const field = fields.find(({ name }) => name === fieldValueItem.fieldName)

    if (!field || isNil(fieldValueItem.fieldValue)) {
      return fieldValueItem
    }

    if (field.fieldType === FieldType.Tag) {
      return {
        ...fieldValueItem,
        fieldValue: fieldValueItem.fieldValue.map?.((value) => ({
          label: value.title,
          value: value.title,
          description: value.description
        }))
      }
    }

    if (fieldValueItem.wysiwyg) {
      return { ...fieldValueItem, fieldValue: fieldValueItem.fieldValue }
    }

    if (field.fieldType === FieldType.Lookup) {
      if (field.multiple && Array.isArray(fieldValueItem.fieldValue)) {
        return {
          ...fieldValueItem,
          fieldValue: fieldValueItem.fieldValue
            .filter((item) => !isEmpty(item))
            .map((item) => ({
              ...item,
              ...getRiskFieldOptionTemplateId(item, lookupOptions),
              label: getRiskFieldOptionLabel(item, lookupOptions, field.fieldType)
            }))
        }
      }

      return {
        ...fieldValueItem,
        fieldValue: {
          ...fieldValueItem.fieldValue,
          ...getRiskFieldOptionTemplateId(fieldValueItem.fieldValue, lookupOptions),
          label: getRiskFieldOptionLabel(fieldValueItem.fieldValue, lookupOptions, field.fieldType)
        }
      }
    }

    if (field.fieldType === FieldType.PickList) {
      const fieldOptions = field.responseType
        ? responseTypeSets?.find((item) => item.id == field.responseTypeId)?.options || []
        : field.options

      if (field.multiple && Array.isArray(fieldValueItem.fieldValue)) {
        return {
          ...fieldValueItem,
          fieldValue: fieldValueItem.fieldValue.map((item) => ({
            label: getRiskFieldOptionLabel(item, fieldOptions, field.fieldType),
            value: item.value
          }))
        }
      }

      return {
        ...fieldValueItem,
        fieldValue: {
          value: getOr(fieldValueItem.fieldValue, 'fieldValue.value', fieldValueItem),
          label: getRiskFieldOptionLabel(fieldValueItem.fieldValue, fieldOptions, field.fieldType)
        }
      }
    }

    if (field.fieldType === FieldType.Date && DATE_SPECIAL_TYPES.includes(fieldValueItem.fieldValue?.value)) {
      return {
        ...fieldValueItem,
        fieldValue: getOptionByValue(fieldValueItem.fieldValue.value, DATE_OPTIONS)
      }
    }

    return fieldValueItem
  })

  return fromPairs(
    initialValues
      .filter((item) => !isUndefined(item))
      .map((item) => [
        item.fieldName,
        isRisk
          ? {
              value: item.fieldValue,
              fieldValueId: item.fieldValueId
            }
          : item.fieldValue
      ])
  )
}

export const getOptionByPath = (entity, path, options) => {
  const objectOrValue = get(path, entity)

  return ifElse(objectOrValue, () => {
    if (options) {
      if (options.options) {
        return options.options.find(({ value }) => value === objectOrValue)
      }

      return {
        label: isFunction(options.label) ? options.label(entity) : get(options.label, objectOrValue),
        value: isFunction(options.value) ? options.value(entity) : get(options.value, objectOrValue)
      }
    }

    return {
      label: objectOrValue,
      value: objectOrValue
    }
  })(entity)
}

export const prepareTagValue = (option) => ({
  title: option.label,
  description: prepareHtml(option.description)
})
